import { FC } from 'react';
import { Content, isFilled } from '@prismicio/client';
import { PrismicRichText, SliceComponentProps } from '@prismicio/react';
import { PrismicNextImage, PrismicNextLink } from '@prismicio/next';

/**
 * Props for `Numbers`.
 */
export type NumbersProps = SliceComponentProps<Content.NumbersSlice>;

// ── EyebrowStatCardsWithSources variation ───────────────────────────────────

const EyebrowStatCardsWithSources: FC<{
  slice: Content.NumbersSliceEyebrowStatCardsWithSources;
}> = ({ slice }) => {
  const { eyebrow, title, stats, background_pattern } = slice.primary;

  return (
    <section
      data-slice-type="numbers"
      data-slice-variation="eyebrowStatCardsWithSources"
      className="relative overflow-hidden text-white"
    >
      {isFilled.image(background_pattern) && (
        <PrismicNextImage
          field={background_pattern}
          fill
          className="pointer-events-none absolute inset-0 object-cover opacity-50"
          fallbackAlt=""
        />
      )}

      <div className="relative z-10 mx-auto flex max-w-[900px] flex-col items-center gap-8 px-6 py-16 md:px-10 md:py-20">
        {eyebrow && (
          <div className="inline-flex rounded-full border border-[#00f557] px-5 py-2">
            <span className="text-[14px] font-semibold uppercase leading-[1.2] tracking-wide text-white">
              {eyebrow}
            </span>
          </div>
        )}

        {isFilled.richText(title) && (
          <PrismicRichText
            field={title}
            components={{
              heading1: ({ children }) => (
                <h2 className="text-center text-[32px] font-bold leading-[1.15] md:text-[44px]">
                  {children}
                </h2>
              ),
              heading2: ({ children }) => (
                <h2 className="text-center text-[32px] font-bold leading-[1.15] md:text-[44px]">
                  {children}
                </h2>
              ),
              strong: ({ children }) => (
                <strong className="font-bold text-[#00f557]">{children}</strong>
              ),
            }}
          />
        )}

        <div className="flex w-full flex-col gap-4">
          {stats?.map((stat, i) => (
            <div
              key={i}
              className="flex flex-col gap-3 rounded-2xl border border-white/15 bg-[rgba(0,0,0,0.35)] p-6 backdrop-blur-md"
            >
              {stat.label && (
                <p className="text-[13px] font-medium uppercase tracking-wide text-white/55">
                  {stat.label}
                </p>
              )}
              {isFilled.richText(stat.description) && (
                <div className="text-[15px] leading-[1.55] text-white/95 md:text-[16px]">
                  <PrismicRichText
                    field={stat.description}
                    components={{
                      paragraph: ({ children }) => (
                        <p className="m-0">{children}</p>
                      ),
                      strong: ({ children }) => (
                        <strong className="font-semibold text-white">
                          {children}
                        </strong>
                      ),
                    }}
                  />
                </div>
              )}
              {isFilled.link(stat.source_link) && (
                <PrismicNextLink
                  field={stat.source_link}
                  className="inline-flex w-fit items-center gap-2 text-[14px] font-semibold text-[#00f557] hover:opacity-80"
                >
                  <span aria-hidden>→</span>
                  <span className="underline">
                    {stat.source_link.text || 'Source'}
                  </span>
                </PrismicNextLink>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ── Main component (dispatches by variation) ────────────────────────────────

/**
 * Component for "Numbers" Slices.
 */
const Numbers = ({ slice }: NumbersProps): JSX.Element => {
  if (slice.variation === 'eyebrowStatCardsWithSources') {
    return <EyebrowStatCardsWithSources slice={slice} />;
  }

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="px-5 md:px-16 py-20 backdrop-blur-[15px] bg-white bg-opacity-35 -mt-20 xl:-mt-40"
    >
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 max-w-screen-2xl mx-auto">
        {slice.primary.numbers.map(({ number, description }, index) => (
          <div
            key={`number-${index}`}
            className="flex flex-col items-center pt-[50px] px-6 pb-6 gap-y-4 rounded-[20px] bg-white bg-opacity-30 backdrop-blur-[13px]"
          >
            <h2 className="text-primary-shade-green">{number}</h2>
            <span className="text-regular-light">{description}</span>
          </div>
        ))}
      </div>
    </section>
  );
};

export default Numbers;
